import { useMemo, useState, type ReactNode } from 'react';
import {
  BrandLoading,
  Button,
  Banner,
  Checkbox,
  CodeSearchField,
  FieldLabel,
  Modal,
  TextField,
  type CodeSearchResult,
} from '@scripsteam/scrips-signal-ds';
import { cn } from '@/lib/utils';
import { ApiError } from '@/shared/api/api-client';
import { useAuthStore } from '@/auth/auth-store';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { showServerError, showSuccess } from '@/shared/lib/toast-helpers';
import { useDiagnosisCodeSearch } from '@/shared/services/use-diagnosis-code-search';
import { toCodeSearchResults } from '@/encounter/lib/to-code-search-result';
import {
  useArchiveOrderSet,
  useCreateOrderSet,
  useOrderSet,
  useUpdateOrderSet,
} from '@/settings/services';
import { useOrderSetOrderSearch } from '@/settings/services/use-order-set-order-search';
import type { OrderSetDetail } from '@/types/order-set-api';
import {
  addItem,
  buildCreateBody,
  buildUpdateBody,
  draftError,
  draftFromDetail,
  emptyDraft,
  isDraftDirty,
  ordersHeader,
  type OrderSetDraft,
  type OrderSetDraftItem,
} from './order-set-draft';
import {
  formatItemCode,
  formatItemDetails,
  hasOrderForm,
  itemToPayload,
  itemTypeTag,
  orderSetTypeLucide,
  pickCoding,
  withSourceSystem,
  type ItemFromPayload,
} from './order-set-item-details';
import { OrderSetOrderForm, type OrderFormRequest } from './order-set-order-form';
import { X } from 'lucide-react';

/**
 * Add / edit an order set (PROD-2710, PROD-2714). ONE Signal DS Modal (lg, 640px) with ONE Save
 * and, when editing, a two-click Archive: the Macro dialog's shape (`../macro/macro-edit-dialog.tsx`).
 * Design: Samer's v3 canvas, EditModal board ("4 · Add / edit: one Modal (lg 640), Save / Cancel,
 * Archive two-click like Macro").
 *
 * `orderSetId` null = "Add order set" (a POST); an id = "Edit order set" (a PUT with
 * `expectedVersion`).
 *
 * Orders: the one search "Search labs, imaging, medications, procedures, vaccines, education" runs
 * the A/P's own catalogue searches (`useOrderSetOrderSearch`). A pick opens the A/P's order form
 * for that type, the same way a rail pick does, and lands as a row ticked Add by default; an
 * education article has no form in the A/P either and lands directly. Pressing a row reopens that
 * same form on the row's details (`OrderSetOrderForm`); its Save rewrites them. There is no pencil:
 * Andrew's decision, 2026-09-30 (PROD-2717), "remove the pencil but open the edit by pressing over
 * the cards", supersedes the pencil on the EditModal board. Education, referral and "other" rows
 * have no form, so they are not pressable. Each row shows its
 * details line, code line and type tag (`order-set-item-details.ts`). There is no reorder: rows
 * stay in the order added.
 *
 * Geometry (PROD-2738, the parity pass): the board's inline values mapped to Signal DS tokens. The
 * DS Modal draws no Close button and titles at 18px, so the board's header (a 17px title with a
 * 28px Close on the right) is drawn here; the DS title stays for the dialog's accessible name,
 * visually hidden. The footer gets the board's hairline rule above it (16px, `border-subtle`).
 *
 * The caller MOUNTS this per open (`key`), so every open starts a fresh draft; a (re)loaded set
 * restarts it too. Errors use the standard toast (`showServerError`). A 409 (the set changed since
 * it was opened) keeps the modal open and offers Reload.
 */
export interface OrderSetEditDialogProps {
  /** null = a new set. */
  orderSetId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const HELP =
  'Select an order to open the same order form as A/P; its details become the set’s defaults. Untick Add by default for orders you only sometimes need.';
const OPTIONAL_LABEL = 'Add by default (optional: shown under the diagnosis to add in one click)';

export function OrderSetEditDialog({ orderSetId, open, onOpenChange }: OrderSetEditDialogProps) {
  const adding = orderSetId === null;
  const detailQ = useOrderSet(open && !adding ? orderSetId : null);
  const detail = detailQ.data;
  const create = useCreateOrderSet();
  const update = useUpdateOrderSet();
  const archive = useArchiveOrderSet();

  // The draft and the set it started from (null base = a new set). A different set object (first
  // load, Reload) restarts it, adjusted during render rather than in an effect.
  const [session, setSession] = useState<{ base: OrderSetDetail | null; draft: OrderSetDraft } | null>(
    () => (adding ? { base: null, draft: emptyDraft() } : null),
  );
  const [conflict, setConflict] = useState(false);
  const [confirmingArchive, setConfirmingArchive] = useState(false);
  // PROD-2830: a new set opens with an empty name, so its "Name is required." waits until the field
  // has been left empty or a save was attempted. An existing set shows it as soon as it is cleared.
  const [nameTouched, setNameTouched] = useState(!adding);
  const restart = (base: OrderSetDetail) => {
    setSession({ base, draft: draftFromDetail(base) });
    setConflict(false);
    setConfirmingArchive(false);
  };
  if (!adding && detail && session?.base !== detail) restart(detail);
  const draft = session?.draft ?? null;
  const setDraft = (next: OrderSetDraft) =>
    setSession((prev) => (prev ? { ...prev, draft: next } : prev));

  const close = () => onOpenChange(false);
  const pending = create.isPending || update.isPending || archive.isPending;
  const error = draft ? draftError(draft) : null;

  const handleSave = () => {
    if (!draft) return;
    if (error) {
      setNameTouched(true);
      return;
    }
    if (adding) {
      create.mutate(buildCreateBody(draft), {
        onSuccess: () => {
          showSuccess('Order set saved');
          close();
        },
        onError: (err) => showServerError('order-sets.create', err),
      });
      return;
    }
    if (!detail) return;
    if (!isDraftDirty(detail, draft)) {
      close();
      return;
    }
    update.mutate(
      { id: detail.id, body: buildUpdateBody(detail, draft) },
      {
        onSuccess: () => {
          showSuccess('Order set saved');
          close();
        },
        onError: (err) => {
          showServerError('order-sets.save', err);
          if (err instanceof ApiError && err.status === 409) setConflict(true);
          if (err instanceof ApiError && err.status === 404) close();
        },
      },
    );
  };

  const handleArchive = () => {
    if (!detail) return;
    if (!confirmingArchive) {
      setConfirmingArchive(true);
      return;
    }
    archive.mutate(detail.id, {
      onSuccess: () => {
        showSuccess('Order set archived');
        close();
      },
      onError: (err) => {
        setConfirmingArchive(false);
        showServerError('order-sets.archive', err);
        if (err instanceof ApiError && err.status === 404) close();
      },
    });
  };

  const handleReload = () => {
    void detailQ.refetch().then((res) => {
      // Restart by hand: structural sharing hands back the SAME object when nothing changed.
      if (res.data) restart(res.data);
    });
  };

  const loading = !adding && (detailQ.isLoading || (!draft && !detailQ.error));
  const failed = !adding && !loading && (!!detailQ.error || !draft || !detail);

  const title = adding ? 'Add order set' : 'Edit order set';

  return (
    <Modal
      open={open}
      onClose={close}
      title={title}
      titleHidden
      size="lg"
      footerAlign="between"
      footer={
        // The board's footer: Save + Cancel on the left, Archive on the right, over a rule.
        <div className="flex flex-1 items-center justify-between gap-2 pt-4 border-t border-border-subtle">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="compact"
              className="px-4"
              onClick={handleSave}
              disabled={!draft || !!error || pending || conflict}
            >
              {create.isPending || update.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button type="button" variant="secondary" size="compact" className="px-4" onClick={close} disabled={pending}>
              Cancel
            </Button>
          </div>
          {!adding && detail && (
            <Button
              type="button"
              variant="tertiary"
              size="compact"
              onClick={handleArchive}
              disabled={pending}
              className={cn(
                'px-3',
                confirmingArchive
                  ? 'bg-status-error text-text-inverse hover:bg-status-error/90'
                  : 'text-status-error hover:text-status-error hover:bg-status-error/10',
              )}
            >
              {confirmingArchive ? 'Confirm Archive' : 'Archive'}
            </Button>
          )}
        </div>
      }
    >
      {/* The board's header. Hidden from assistive tech: the DS title above names the dialog. */}
      <div className="flex items-start justify-between gap-3">
        <p aria-hidden="true" className="m-0 text-title font-semibold text-text-primary">
          {title}
        </p>
        <button
          type="button"
          aria-label="Close"
          onClick={close}
          className="icon-action shrink-0"
        >
          <X className="size-4" />
        </button>
      </div>
      <div className="mt-4 space-y-4.5 max-h-overlay-body overflow-y-auto scrollbar-hide">
        {loading ? (
          <BrandLoading size={48} label="Loading order set…" />
        ) : failed || !draft ? (
          <p className="text-sm text-status-error">Failed to load this order set. Please try again.</p>
        ) : (
          <>
            {conflict && (
              <Banner
                variant="warning"
                title="This order set changed"
                role="alert"
                data-slot="order-set-conflict"
                action={
                  <Button type="button" variant="secondary" size="sm" onClick={handleReload} disabled={detailQ.isFetching}>
                    {detailQ.isFetching ? 'Reloading…' : 'Reload'}
                  </Button>
                }
              >
                It was changed since you opened it. Reload to edit the latest version.
              </Banner>
            )}
            <OrderSetForm
              formId={detail?.id ?? 'new'}
              adding={adding}
              draft={draft}
              onChange={setDraft}
              disabled={pending || conflict}
              showNameError={nameTouched}
              onNameBlur={() => setNameTouched(true)}
            />
          </>
        )}
      </div>
    </Modal>
  );
}

function OrderSetForm({
  formId,
  adding,
  draft,
  onChange,
  disabled,
  showNameError,
  onNameBlur,
}: {
  formId: string;
  adding: boolean;
  draft: OrderSetDraft;
  onChange: (next: OrderSetDraft) => void;
  disabled: boolean;
  /** False until the name was touched or a save attempted (a new set); always true when editing. */
  showNameError: boolean;
  onNameBlur: () => void;
}) {
  const nameMissing = draft.name.trim().length === 0;

  return (
    <>
      <div>
        {/* The board's mark sits 2px after the label; the DS `required` marker sits 4px. */}
        <FieldLabel stacked className="mb-2" htmlFor={`order-set-name-${formId}`}>
          Name
          <span aria-hidden="true" className="ml-1 text-status-error">
            *
          </span>
        </FieldLabel>
        <TextField
          id={`order-set-name-${formId}`}
          size="compact"
          aria-required
          value={draft.name}
          maxLength={100}
          disabled={disabled}
          error={showNameError && nameMissing ? 'Name is required.' : undefined}
          onBlur={onNameBlur}
          onChange={(e) => onChange({ ...draft, name: e.target.value })}
        />
      </div>

      <DiagnosesField draft={draft} onChange={onChange} disabled={disabled} />

      <OrdersField draft={draft} onChange={onChange} disabled={disabled} adding={adding} />
    </>
  );
}

function DiagnosesField({
  draft,
  onChange,
  disabled,
}: {
  draft: OrderSetDraft;
  onChange: (next: OrderSetDraft) => void;
  disabled: boolean;
}) {
  const [query, setQuery] = useState('');
  const debounced = useDebounce(query, 250);
  const search = useDiagnosisCodeSearch(debounced, !disabled);

  const taken = useMemo(() => new Set(draft.diagnoses.map((d) => d.code)), [draft.diagnoses]);
  const results = useMemo(
    () =>
      query.trim().length === 0
        ? []
        : toCodeSearchResults(search.data).filter((r) => !!r.code && !taken.has(r.code)),
    [query, search.data, taken],
  );

  const add = (r: CodeSearchResult) => {
    if (!r.code || taken.has(r.code)) return;
    onChange({
      ...draft,
      diagnoses: [...draft.diagnoses, { system: 'ICD-10', code: r.code, display: r.display }],
    });
    setQuery('');
  };

  return (
    <div>
      <FieldLabel stacked className="mb-2">
        Diagnoses (optional)
      </FieldLabel>
      {draft.diagnoses.length > 0 && (
        <ul className="mb-2 flex flex-wrap gap-2" aria-label="Diagnoses">
          {draft.diagnoses.map((d) => (
            <li
              key={d.code}
              data-slot="order-set-dx-chip"
              className="inline-flex items-center gap-2 h-8 pl-2 pr-1 rounded-md bg-surface-background text-sm text-text-default"
            >
              <span data-slot="order-set-dx-chip-code" className="font-mono text-size-eyebrow text-grey-800">{d.code}</span>
              {d.display}
              <button
                type="button"
                aria-label={`Remove ${d.code}`}
                disabled={disabled}
                onClick={() =>
                  onChange({ ...draft, diagnoses: draft.diagnoses.filter((x) => x.code !== d.code) })
                }
                className="inline-flex h-5 w-5 items-center justify-center rounded-control text-text-muted hover:bg-grey-100 hover:text-brand-primary"
              >
                <X className="size-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <CodeSearchField
        value={query}
        onChange={setQuery}
        results={results}
        loading={search.isFetching && query.trim().length > 0}
        disabled={disabled}
        placeholder="Add a diagnosis by code or name"
        onResultClick={add}
      />
      <p className="mt-1 text-xs text-text-muted">
        A/P shows this set first when the visit has one of these diagnoses.
      </p>
    </div>
  );
}

/** The open order form: which request, and for a pressed row, which row it writes back to. */
interface OpenForm {
  request: OrderFormRequest;
  /** The row that was pressed; absent for a new pick. */
  itemKey?: string;
}

function OrdersField({
  draft,
  onChange,
  disabled,
  adding,
}: {
  draft: OrderSetDraft;
  onChange: (next: OrderSetDraft) => void;
  disabled: boolean;
  /** A new set starts with no orders: Save waits for one, with no error on open. */
  adding: boolean;
}) {
  const user = useAuthStore((s) => s.user);
  // The A/P's own provider id, for the order forms' provider-scoped lookups (encounter-shell-page.tsx).
  // The search itself is global and does not need it (PROD-2739).
  const providerId = user?.practitionerId ?? user?.id;
  const [query, setQuery] = useState('');
  const search = useOrderSetOrderSearch(query, !disabled);
  const [form, setForm] = useState<OpenForm | null>(null);

  const setItems = (items: OrderSetDraftItem[]) => onChange({ ...draft, items });

  const pick = (result: CodeSearchResult) => {
    const p = search.resolve(result);
    setQuery('');
    if (!p) return;
    if (p.type === 'education') {
      // The A/P adds an article with no form (ap-search-rail `onAddArticle`); so does the set.
      onChange(
        addItem(draft, {
          type: 'education',
          code: { system: '', code: p.item.articleUrl || p.item.display, display: p.item.display },
          details: {
            ...(p.item.articleUrl ? { articleUrl: p.item.articleUrl } : {}),
            ...(p.item.summary ? { summary: p.item.summary } : {}),
          },
        }),
      );
      return;
    }
    setForm({ request: { mode: 'add', pick: p } });
  };

  const openItem = (item: OrderSetDraftItem) => {
    const payload = itemToPayload({ ...item, itemId: item.itemId ?? item.key });
    if (!payload) return;
    setForm({ request: { mode: 'edit', payload }, itemKey: item.key });
  };

  const handleFormSave = (saved: ItemFromPayload) => {
    const opened = form;
    setForm(null);
    if (!opened) return;
    // PROD-2828: keep the coding system of the search pick (or of the row being edited) when the
    // order form did not write one back, e.g. a DDC medication.
    const source = opened.itemKey
      ? draft.items.find((it) => it.key === opened.itemKey)?.code
      : opened.request.mode === 'add'
        ? pickCoding(opened.request.pick)
        : undefined;
    const result = withSourceSystem(saved, source);
    if (opened.itemKey) {
      setItems(
        draft.items.map((it) =>
          it.key === opened.itemKey ? { ...it, code: result.code, details: result.details } : it,
        ),
      );
    } else {
      onChange(addItem(draft, result));
    }
  };

  return (
    <div>
      <FieldLabel stacked className="mb-2">
        {ordersHeader(draft)}
      </FieldLabel>
      <div className="mb-3">
        <CodeSearchField
          value={query}
          onChange={setQuery}
          results={[]}
          groups={query.trim() ? search.groups : []}
          loading={search.loading}
          disabled={disabled}
          placeholder="Search labs, imaging, medications, procedures, vaccines, education"
          onResultClick={pick}
        />
      </div>
      {draft.items.length === 0 ? (
        adding ? null : <p className="text-sm text-status-error">An order set needs at least one order.</p>
      ) : (
        <ol className="flex flex-col gap-2" aria-label="Orders">
          {draft.items.map((item) => (
            <OrderRow
              key={item.key}
              item={item}
              disabled={disabled}
              onToggle={(addByDefault) =>
                setItems(draft.items.map((it) => (it.key === item.key ? { ...it, addByDefault } : it)))
              }
              onOpen={hasOrderForm(item.type) ? () => openItem(item) : undefined}
              onRemove={() => setItems(draft.items.filter((it) => it.key !== item.key))}
            />
          ))}
        </ol>
      )}
      <p className="mt-2 text-xs text-text-muted">{HELP}</p>

      {form && (
        <OrderSetOrderForm
          request={form.request}
          providerId={providerId}
          onSave={handleFormSave}
          onCancel={() => setForm(null)}
        />
      )}
    </div>
  );
}

function OrderRow({
  item,
  disabled,
  onToggle,
  onOpen,
  onRemove,
}: {
  item: OrderSetDraftItem;
  disabled: boolean;
  onToggle: (addByDefault: boolean) => void;
  /**
   * Pressing the row opens its A/P order form (PROD-2717). Absent for a type the A/P has no order
   * form for (education, referral, other): the row is then not pressable.
   */
  onOpen?: () => void;
  onRemove: () => void;
}) {
  const name = item.code.display || item.code.code;
  const code = formatItemCode(item);
  const details = formatItemDetails(item);
  const TypeGlyph = orderSetTypeLucide(item.type);

  // PROD-2717: the row opens on press. ONE native button laid over the row (absolute, inset 0, named
  // `Edit <name>`), a SIBLING of the checkbox and the remove button, never a wrapper, so nothing
  // interactive is nested. Those two controls are lifted above it (`LIFT`) and keep their own
  // actions. The same pattern as the DS `ApOrderCard` `onOpen` the A/P rows use.
  const openable = !!onOpen;
  const above = openable ? LIFT : undefined;

  return (
    <li
      aria-label={name}
      className={cn(
        'flex items-stretch gap-3 pl-2 rounded-md bg-grey-100',
        openable && 'relative',
      )}
    >
      {openable && (
        <button
          type="button"
          data-slot="order-open"
          aria-label={`Edit ${name}`}
          disabled={disabled}
          onClick={onOpen}
          className="absolute inset-0 z-0 m-0 p-0 rounded-md border-0 bg-transparent mix-blend-multiply enabled:hover:bg-grey-200 cursor-pointer disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-primary"
        />
      )}
      <span aria-hidden="true" className="my-2 w-0.5 shrink-0 rounded-tag bg-brand-primary" />
      <span className="flex items-start pt-3 text-text-secondary">
        <TypeGlyph className="size-4" aria-hidden />
      </span>
      <div className="flex-1 min-w-0 flex flex-col gap-1 py-2">
        <span className="flex flex-wrap items-baseline gap-2">
          <span data-slot="order-row-name" className="text-size-body-15 leading-body text-text-primary">{name}</span>
          {code && <span data-slot="order-row-code" className="text-size-sm font-bold text-text-hint">{code}</span>}
          <span data-slot="order-type-tag" className="px-1 py-0.5 rounded-tag bg-status-info-bg text-brand-depth text-size-2xs font-bold tracking-label">
            {itemTypeTag(item)}
          </span>
        </span>
        {details && <span className="text-size-sm leading-body text-text-secondary">{details}</span>}
        <div className={cn('mt-2 self-start', above)}>
          {/* The board's label: 12px secondary, 6px after the box (the DS label is 13px primary). */}
          <Checkbox
            checked={item.addByDefault}
            disabled={disabled}
            onCheckedChange={onToggle}
            className="gap-2"
            label={
              <span className="text-size-eyebrow text-text-secondary">
                {item.addByDefault ? 'Add by default' : OPTIONAL_LABEL}
              </span>
            }
          />
        </div>
      </div>
      <div className={cn('flex items-start gap-1 pt-2 pr-2', above)}>
        <RowButton label={`Remove ${name}`} disabled={disabled} onClick={onRemove}>
          <X className="size-4" />
        </RowButton>
      </div>
    </li>
  );
}

/** Raises a row control above the open overlay (a positioned later sibling paints on top). */
const LIFT = 'relative z-1';

function RowButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="inline-flex h-8 w-8 items-center justify-center rounded-control text-text-muted hover:bg-grey-200 hover:text-brand-primary disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-text-muted"
    >
      {children}
    </button>
  );
}
